// File: src/components/modals/EditStudentModal.jsx

import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  X, User, Phone, Mail, MapPin, CheckCircle, AlertCircle,
  Save, Car, BookOpen, Building2, Calendar, Clock
} from 'lucide-react';
import { studentAPI } from '../../services/api';
import { toast } from '../../utils/toast';

// ── Theory test expiry helper ─────────────────────────────────────────────────
const isTheoryExpired = (dateStr) => {
  if (!dateStr) return false;
  const passed   = new Date(dateStr);
  const twoYears = new Date();
  twoYears.setFullYear(twoYears.getFullYear() - 2);
  return passed < twoYears;
};

const SectionHead = ({ icon: Icon, label }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: '0.4rem',
    borderBottom: '1.5px solid #e5e7eb', paddingBottom: '0.35rem',
    marginBottom: '0.75rem', marginTop: '0.5rem'
  }}>
    <Icon size={14} color="#6366f1" />
    <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#6366f1',
      letterSpacing: '0.07em', textTransform: 'uppercase' }}>{label}</span>
  </div>
);

const EditStudentModal = ({ student, onClose, onSuccess }) => {
  const { getAccessTokenSilently } = useAuth0();

  const [formData, setFormData] = useState({
    progressLevel: 1,
    status: '',
    phone: '',
    email: '',
    pickupAddress: '',
    pickupPostcode: '',
    dropoffAddress: '',
    dropoffPostcode: '',
    vehicleOption: 'Automatic',
    theoryTestCompletedDate: '',
    testCenter: '',
    testDate: '',
    testTime: ''
  });

  const [isSaving, setIsSaving]       = useState(false);
  const [sameAsPickup, setSameAsPickup] = useState(false);

  useEffect(() => {
    if (student) {
      setFormData({
        progressLevel:          student.progressLevel ?? 1,
        status:                 student.status || 'active',
        phone:                  student.phone || '',
        email:                  student.email || '',
        pickupAddress:          student.pickupAddress || '',
        pickupPostcode:         student.pickupPostcode || '',
        dropoffAddress:         student.dropoffAddress || '',
        dropoffPostcode:        student.dropoffPostcode || '',
        vehicleOption:          student.vehicleOption || 'Automatic',
        theoryTestCompletedDate: student.theoryTestCompletedDate || '',
        testCenter:             student.testCenter || '',
        testDate:               student.testDate || '',
        testTime:               student.testTime || ''
      });
      // Pre-tick "Same as Pickup" if addresses already match
      setSameAsPickup(
        !!(student.pickupAddress &&
           student.pickupAddress === student.dropoffAddress &&
           student.pickupPostcode === student.dropoffPostcode)
      );
    }
  }, [student]);

  const set = (name, value) => {
    setFormData(p => {
      const updated = { ...p, [name]: value };
      // Keep dropoff in sync while checkbox is ticked
      if (sameAsPickup) {
        if (name === 'pickupAddress')  updated.dropoffAddress  = value;
        if (name === 'pickupPostcode') updated.dropoffPostcode = value;
      }
      return updated;
    });
  };

  const handleSameAsPickup = (checked) => {
    setSameAsPickup(checked);
    if (checked) {
      setFormData(p => ({
        ...p,
        dropoffAddress:  p.pickupAddress,
        dropoffPostcode: p.pickupPostcode
      }));
    }
  };

  const handleInputChange = (e) => set(e.target.name, e.target.value);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      const updates = {};
      const diff = (key, orig) => {
        if (formData[key] !== (orig ?? '')) updates[key] = formData[key];
      };

      diff('progressLevel', student.progressLevel ?? 1);
      if (formData.status !== student.status) updates.status = formData.status;
      // phone: never editable — excluded from updates
      if (student.registrationStatus !== 'registered') diff('email', student.email);
      diff('pickupAddress',          student.pickupAddress);
      diff('pickupPostcode',         student.pickupPostcode);
      diff('dropoffAddress',         student.dropoffAddress);
      diff('dropoffPostcode',        student.dropoffPostcode);
      diff('vehicleOption',          student.vehicleOption);
      diff('theoryTestCompletedDate', student.theoryTestCompletedDate);
      diff('testCenter',             student.testCenter);
      diff('testDate',               student.testDate);
      diff('testTime',               student.testTime);

      if (Object.keys(updates).length === 0) {
        toast.info('No changes detected');
        setIsSaving(false);
        return;
      }

      const response = await studentAPI.updateStudentFields(
        student.studentId || student.id,
        updates,
        getAccessTokenSilently
      );

      if (response.success) {
        toast.success('Student updated successfully!');
        if (onSuccess) setTimeout(() => { onSuccess(); onClose(); }, 1200);
      } else {
        toast.error(response.message || 'Failed to update student');
      }
    } catch (err) {
      toast.error(err.message || 'Failed to update student. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const theoryExpired = isTheoryExpired(formData.theoryTestCompletedDate);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-medium" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="modal-header">
          <div className="modal-header-content">
            <User size={24} />
            <h2>Edit Student</h2>
          </div>
          <button className="close-button" onClick={onClose}><X size={24} /></button>
        </div>

        {/* Body */}
        <div className="modal-body">
          <form onSubmit={handleSubmit} className="edit-form">

            {/* Read-only name */}
            <div className="form-section">
              <div className="form-group">
                <label className="form-label-readonly">Student Name</label>
                <input type="text" value={student.name || 'Unknown'}
                  disabled className="form-input-readonly" />
              </div>
            </div>

            {/* ── Status + Progress ─────────────────────────────────────────── */}
            <SectionHead icon={CheckCircle} label="Status & Progress" />

            <div className="form-section">
              <label className="form-label">Status</label>
              <div className="status-toggle-group">
                {[
                  { val: 'active',   label: 'Active' },
                  { val: 'inactive', label: 'Inactive' },
                  { val: 'passed',   label: 'Passed' }
                ].map(({ val, label }) => (
                  <button key={val} type="button"
                    className={`status-toggle-btn ${formData.status === val ? val : ''}`}
                    onClick={() => set('status', val)}>
                    {val === 'inactive' ? <X size={16} /> : <CheckCircle size={16} />}
                    {label}
                  </button>
                ))}
              </div>
              <p className="form-hint">
                Current: <strong>{student.status === 'approve' ? 'Active (Approved)' : student.status}</strong>
              </p>
            </div>

          {/* There won't be multiple level options
            <div className="form-section">
              <div className="form-group">
                <label className="form-label">Progress Level</label>
                <select name="progressLevel" value={formData.progressLevel}
                  onChange={handleInputChange} className="form-input" style={{ cursor: 'pointer' }}>
                  {Array.from({ length: 6 }, (_, i) => i).map(n => (
                    <option key={n} value={n}>Level {n}</option>
                  ))}
                </select>
                <p className="form-hint">Current: <strong>Level {student.progressLevel ?? 1}</strong></p>
              </div>
            </div>
            */}

            {/* ── Vehicle ───────────────────────────────────────────────────── */}
            <SectionHead icon={Car} label="Vehicle Preference" />

            <div className="form-section">
              <div className="status-toggle-group">
                {['Automatic', 'Manual'].map(opt => (
                  <button key={opt} type="button"
                    className={`status-toggle-btn vehicle-btn ${formData.vehicleOption === opt ? opt.toLowerCase() : ''}`}
                    onClick={() => set('vehicleOption', opt)}>
                    <Car size={16} /> {opt}
                  </button>
                ))}
              </div>
              <p className="form-hint">Current: <strong>{student.vehicleOption || 'Not set'}</strong></p>
            </div>

            {/* ── Contact ───────────────────────────────────────────────────── */}
            <SectionHead icon={Phone} label="Contact Details" />

            <div className="form-section">
              <div className="form-group">
                <label className="form-label"><Phone size={14} /> Phone Number</label>
                <input type="tel" name="phone" value={formData.phone}
                  readOnly disabled className="form-input-readonly"
                  style={{ background: '#f9fafb', cursor: 'not-allowed' }} />
                <p className="form-hint" style={{ marginTop: '0.2rem' }}>
                  Phone number cannot be changed here.
                </p>
              </div>
              <div className="form-group" style={{ marginTop: '0.75rem' }}>
                <label className="form-label"><Mail size={14} /> Email Address</label>
                <input type="email" name="email" value={formData.email}
                  onChange={student.registrationStatus === 'registered' ? undefined : handleInputChange}
                  readOnly={student.registrationStatus === 'registered'}
                  disabled={student.registrationStatus === 'registered'}
                  className={student.registrationStatus === 'registered' ? 'form-input-readonly' : 'form-input'}
                  style={student.registrationStatus === 'registered'
                    ? { background: '#f9fafb', cursor: 'not-allowed' }
                    : {}}
                  placeholder="student@example.com" />
                {student.registrationStatus === 'registered' && (
                  <p className="form-hint" style={{ marginTop: '0.2rem' }}>
                    Email cannot be changed after registration.
                  </p>
                )}
              </div>
            </div>

            {/* ── Pickup Address ────────────────────────────────────────────── */}
            <SectionHead icon={MapPin} label="Pickup Address" />

            <div className="form-section">
              <div className="address-group">
                <div className="form-group">
                  <label className="form-sublabel">Street Address</label>
                  <input type="text" name="pickupAddress" value={formData.pickupAddress}
                    onChange={handleInputChange} className="form-input"
                    placeholder="80 Burden Drive" />
                </div>
                <div className="form-group">
                  <label className="form-sublabel">Postcode</label>
                  <input type="text" name="pickupPostcode" value={formData.pickupPostcode}
                    onChange={handleInputChange} className="form-input" placeholder="SP1 3RD" />
                </div>
              </div>
            </div>

            {/* ── Dropoff Address ───────────────────────────────────────────── */}
            {/* Section header + Same as Pickup toggle on the same row */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              borderBottom: '1.5px solid #e5e7eb', paddingBottom: '0.35rem',
              marginBottom: '0.75rem', marginTop: '0.5rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <MapPin size={14} color="#6366f1" />
                <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#6366f1',
                  letterSpacing: '0.07em', textTransform: 'uppercase' }}>Drop Off Address</span>
              </div>
              <label style={{
                display: 'flex', alignItems: 'center', gap: '0.4rem',
                cursor: 'pointer', fontSize: '0.78rem', color: '#6366f1',
                fontWeight: 600, userSelect: 'none'
              }}>
                <input type="checkbox" checked={sameAsPickup}
                  onChange={e => handleSameAsPickup(e.target.checked)}
                  style={{ accentColor: '#6366f1', width: '14px', height: '14px', cursor: 'pointer' }} />
                Same as Pickup Address
              </label>
            </div>

            <div className="form-section">
              <div className="address-group">
                <div className="form-group">
                  <label className="form-sublabel">Street Address</label>
                  <input type="text" name="dropoffAddress" value={formData.dropoffAddress}
                    onChange={handleInputChange}
                    disabled={sameAsPickup}
                    className={sameAsPickup ? 'form-input-readonly' : 'form-input'}
                    style={sameAsPickup ? { background: '#f9fafb', cursor: 'not-allowed' } : {}}
                    placeholder="80 Burden Drive" />
                </div>
                <div className="form-group">
                  <label className="form-sublabel">Postcode</label>
                  <input type="text" name="dropoffPostcode" value={formData.dropoffPostcode}
                    onChange={handleInputChange}
                    disabled={sameAsPickup}
                    className={sameAsPickup ? 'form-input-readonly' : 'form-input'}
                    style={sameAsPickup ? { background: '#f9fafb', cursor: 'not-allowed' } : {}}
                    placeholder="SP1 3RD" />
                </div>
              </div>
            </div>

            {/* ── Driving Test Details ──────────────────────────────────────── */}
            <SectionHead icon={Calendar} label="Driving Test Details" />

            <div className="form-section">
              {/* Theory test date + expiry indicator */}
              <div className="form-group" style={{ marginBottom: '0.75rem' }}>
                <label className="form-label">
                  <BookOpen size={14} /> Theory Test Passed Date
                  {theoryExpired && (
                    <span style={{
                      marginLeft: '0.5rem', fontSize: '0.72rem', fontWeight: 700,
                      color: '#ef4444', background: '#fee2e2',
                      padding: '0.15rem 0.5rem', borderRadius: '999px'
                    }}>
                      Expired
                    </span>
                  )}
                </label>
                <input type="date" name="theoryTestCompletedDate"
                  value={formData.theoryTestCompletedDate}
                  onChange={handleInputChange} className="form-input"
                  max={new Date().toISOString().split('T')[0]}
                  style={theoryExpired ? { borderColor: '#ef4444' } : {}} />
                {theoryExpired && (
                  <p className="form-hint" style={{ color: '#ef4444' }}>
                    Theory test result is older than 2 years and has expired.
                  </p>
                )}
              </div>

              {/* Test centre */}
              <div className="form-group" style={{ marginBottom: '0.75rem' }}>
                <label className="form-label"><Building2 size={14} /> Test Centre</label>
                <input type="text" name="testCenter" value={formData.testCenter}
                  onChange={handleInputChange} className="form-input"
                  placeholder="e.g. Salisbury Test Centre" />
              </div>

              {/* Test date + time on same row */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <div className="form-group">
                  <label className="form-label"><Calendar size={14} /> Test Date</label>
                  <input type="date" name="testDate" value={formData.testDate}
                    onChange={handleInputChange} className="form-input"
                    min={new Date().toISOString().split('T')[0]} />
                </div>
                <div className="form-group">
                  <label className="form-label"><Clock size={14} /> Test Time</label>
                  <input type="time" name="testTime" value={formData.testTime}
                    onChange={handleInputChange} className="form-input" />
                </div>
              </div>
            </div>

          </form>
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={isSaving}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" onClick={handleSubmit} disabled={isSaving}>
            {isSaving
              ? <><div className="btn-spinner"></div>Saving…</>
              : <><Save size={18} />Save Changes</>
            }
          </button>
        </div>

      </div>
    </div>
  );
};

export default EditStudentModal;